import React, { useEffect, useMemo, useRef, useState } from 'react';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';

import { Box, IconButton, SearchField, Typography } from '@/shared/ui';
import { formatPersonName } from '@/shared/utils/personName';
import { radius } from '@/shared/theme/tokens';
import StaffIdentity, { staffPickerInitials } from '../data-display/StaffIdentity';

export interface StaffPickerOption {
  _id: string;
  firstName: string;
  lastName: string;
  staffCode?: string;
  designation?: string;
}

export type StaffAssigneePickerVariant = 'compact' | 'icon';

type StaffLike = {
  _id?: string;
  id?: string;
  firstName: string;
  lastName: string;
  staffCode?: string;
  designation?: string;
};

export const toStaffPickerOption = (staff: StaffLike | null | undefined): StaffPickerOption | null => {
  if (!staff) return null;
  const id = staff._id ?? staff.id;
  if (!id) return null;
  return {
    _id: id,
    firstName: staff.firstName,
    lastName: staff.lastName,
    staffCode: staff.staffCode,
    designation: staff.designation,
  };
};

export { staffPickerInitials };

export const formatStaffPickerSecondary = (staff: StaffPickerOption): string | null => {
  const parts = [staff.staffCode, staff.designation].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

const dashedIconSx = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '1px dashed',
  borderColor: 'divider',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.disabled',
  flexShrink: 0,
} as const;

export interface StaffAssigneePickerProps {
  value: StaffPickerOption | null;
  options: StaffPickerOption[];
  onChange: (staff: StaffPickerOption | null) => void;
  ariaLabel: string;
  variant?: StaffAssigneePickerVariant;
  /** Compact trigger shows designation on a second line when the staff record has one. */
  showDesignation?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  searchPlaceholder?: string;
  avatarSize?: number;
  anchorOrigin?: React.ComponentProps<typeof Popover>['anchorOrigin'];
  transformOrigin?: React.ComponentProps<typeof Popover>['transformOrigin'];
}

/**
 * Inline staff assignee picker: compact trigger (avatar + name) or icon-only,
 * with searchable popover list (avatar, name, staff code · designation, checkmark).
 */
const StaffAssigneePicker: React.FC<StaffAssigneePickerProps> = ({
  value,
  options,
  onChange,
  ariaLabel,
  variant = 'compact',
  showDesignation = false,
  disabled = false,
  allowClear = false,
  searchPlaceholder,
  avatarSize,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  transformOrigin = { vertical: 'top', horizontal: 'left' },
}) => {
  const { t } = useTranslation('staff');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const resolvedAvatarSize = avatarSize ?? (variant === 'icon' ? 28 : 24);
  const open = Boolean(anchorEl);
  const resolvedPlaceholder = searchPlaceholder ?? t('staff:filters.search', { defaultValue: 'staff:filters.search' });

  useEffect(() => {
    if (!open) return;
    setSearch('');
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((staff) => {
      const name = formatPersonName(staff).toLowerCase();
      const code = staff.staffCode?.toLowerCase() ?? '';
      const designation = staff.designation?.toLowerCase() ?? '';
      return name.includes(query) || code.includes(query) || designation.includes(query);
    });
  }, [options, search]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>): void => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleSelect = (staff: StaffPickerOption): void => {
    onChange(staff);
    handleClose();
  };

  const handleClear = (): void => {
    onChange(null);
    handleClose();
  };

  // Keep trigger size stable when open/closed so the list cell does not jump.
  const triggerSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: variant === 'compact' && value ? 1 : 0,
    minWidth: 0,
    maxWidth: '100%',
    border: 0,
    p: 0.25,
    borderRadius: radius.sm,
    bgcolor: open
      ? (theme: { palette: { action: { selected: string } } }) =>
          theme.palette.action.selected
      : 'transparent',
    cursor: 'pointer',
    color: 'inherit',
    '&:hover': { bgcolor: 'action.hover' },
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={triggerSx}
      >
        {value ? (
          <StaffIdentity
            person={value}
            size={variant === 'icon' ? 'md' : 'sm'}
            layout={
              variant === 'icon' ? 'avatar' : showDesignation ? 'block' : 'inline'
            }
          />
        ) : (
          <Box component="span" sx={{ ...dashedIconSx, width: resolvedAvatarSize, height: resolvedAvatarSize }}>
            <PersonOutlineIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 320,
              maxWidth: 'calc(100vw - 32px)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <SearchField
            inputRef={searchRef}
            fullWidth
            placeholder={resolvedPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleClose();
            }}
          />
        </Box>

        <Box
          role="listbox"
          aria-label={ariaLabel}
          sx={{
            maxHeight: 280,
            overflowY: 'auto',
            py: 0.5,
          }}
        >
          {filteredOptions.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 1.5, textAlign: 'center' }}
            >
              {t('picker.noResults')}
            </Typography>
          ) : (
            filteredOptions.map((staff) => {
              const isSelected = value?._id === staff._id;
              const secondary = formatStaffPickerSecondary(staff);
              return (
                <Box
                  key={staff._id}
                  component="button"
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(staff)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    border: 0,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    textAlign: 'left',
                    bgcolor: isSelected
                      ? (theme) => alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    '&:hover': {
                      bgcolor: (theme) =>
                        isSelected
                          ? alpha(theme.palette.primary.main, 0.12)
                          : theme.palette.action.hover,
                    },
                  }}
                >
                  <StaffIdentity
                    person={staff}
                    size="md"
                    secondary={secondary}
                    sx={{ flex: 1 }}
                  />
                  {isSelected ? (
                    <CheckIcon color="primary" sx={{ fontSize: 18, flexShrink: 0 }} />
                  ) : (
                    <Box sx={{ width: 18, flexShrink: 0 }} />
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {allowClear && value ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              borderTop: 1,
              borderColor: 'divider',
              px: 1,
              py: 0.5,
            }}
          >
            <IconButton
              size="small"
              aria-label={t('picker.clearAssignee')}
              onClick={handleClear}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}
      </Popover>
    </>
  );
};

export default StaffAssigneePicker;
